import { Close, ExpandMore, Search } from '@mui/icons-material';
import {
    Avatar,
    Button,
    Checkbox,
    debounce,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    OutlinedInput,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useGetServiceCategoryList, useGetVehicleMakeList } from '../../../../services/core/master/master.hooks';
import useSavedServiceStore from '../../../../store/useSavedServiceStore';

const styles = {
    menuPadding: {
        padding: '0',
        width: 'calc(100% + 10px)!important',
    },
    formControlLabel: {
        padding: '6px 16px',
        marginRight: '0!important',
        width: '100%',
    },
};

const SavedServicesFilters = ({ savedServiceParams, setSavedServiceParams }) => {
    // TanStack Query hooks
    const { data: serviceCategoriesData, isLoading: serviceCategoriesLoading } = useGetServiceCategoryList({
        limit: 1500,
    });

    const { data: vehicleMakeData, isLoading: vehicleMakeLoading } = useGetVehicleMakeList({
        page: 1,
        limit: 2000,
    });

    const [anchorElCategory, setAnchorElCategory] = useState(null);
    const openCategory = Boolean(anchorElCategory);

    const [anchorElVehicle, setAnchorElVehicle] = useState(null);
    const openVehicle = Boolean(anchorElVehicle);

    const activeFilterCount = [savedServiceParams.serviceCategory.length, savedServiceParams.vehicleMake.length].filter((count) => count > 0).length;

    const showClearFilterButton = activeFilterCount > 0;
    const clearFilterLabel = activeFilterCount > 1 ? 'Clear Filters' : 'Clear Filter';

    // Extract data from TanStack Query responses
    const serviceCategories = Array.isArray(serviceCategoriesData?.list)
        ? serviceCategoriesData.list
        : Array.isArray(serviceCategoriesData)
        ? serviceCategoriesData
        : [];

    const vehicleMake = Array.isArray(vehicleMakeData?.data) ? vehicleMakeData.data : Array.isArray(vehicleMakeData) ? vehicleMakeData : [];

    const loading = serviceCategoriesLoading || vehicleMakeLoading;

    const handleResetFilter = () => {
        setSavedServiceParams({
            ...savedServiceParams,
            serviceCategory: [],
            vehicleMake: [],

            page: 1,
        });
    };

    const debounceSearch = useCallback(
        debounce((event) => {
            const latestParams = useSavedServiceStore.getState().savedServiceParams;
            setSavedServiceParams({
                ...latestParams,
                search: event?.target?.value || '',
                page: 1,
            });
        }, 800),
        []
    );

    // Handle category selection (multiple)
    const handleCategoryChange = (category) => {
        const currentCategories = savedServiceParams.serviceCategory || [];
        let updatedCategories;

        if (currentCategories.includes(category?._id)) {
            // Remove if already selected
            updatedCategories = currentCategories.filter((id) => id !== category?._id);
        } else {
            // Add if not selected
            updatedCategories = [...currentCategories, category?._id];
        }
        setSavedServiceParams({
            ...savedServiceParams,
            serviceCategory: updatedCategories,
            page: 1,
        });
    };

    // Handle category selection (multiple)
    const handleVehicleChange = (vehcile) => {
        const currentVehicles = savedServiceParams.vehicleMake || [];
        let updatedVehicles;

        if (currentVehicles.includes(vehcile?._id)) {
            // Remove if already selected
            updatedVehicles = currentVehicles.filter((id) => id !== vehcile?._id);
        } else {
            // Add if not selected
            updatedVehicles = [...currentVehicles, vehcile?._id];
        }
        setSavedServiceParams({
            ...savedServiceParams,
            vehicleMake: updatedVehicles,
            page: 1,
        });
    };

    // Get category button text with count
    const getCategoryButtonText = () => {
        const selectedCount = savedServiceParams.serviceCategory?.length || 0;
        if (selectedCount > 0) {
            return `Category`;
        }
        return 'CATEGORY';
    };

    // Get Vehicle button text with count
    const getVehicleButtonText = () => {
        const selectedCount = savedServiceParams.vehicleMake?.length || 0;
        if (selectedCount > 0) {
            return `Vehicle MAKE`;
        }
        return 'VEHICLE MAKE';
    };

    return (
        <Grid container display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
            {/* Menus Filter  */}
            <Grid container item xs={12} sm={12} md={12} lg={6} xl={6} justifyContent={'flex-start'} alignItems={'center'} rowGap={1} columnGap={2}>
                {/* Status Filter */}
                <Grid item md="auto">
                    <Button
                        variant={savedServiceParams.serviceCategory?.length > 0 ? 'contained' : 'text'}
                        id="category-filter-button"
                        aria-haspopup="true"
                        onClick={(event) => setAnchorElCategory(event.currentTarget)}
                        sx={{
                            color: savedServiceParams.serviceCategory?.length > 0 ? 'white!important' : 'black!important',
                            fontWeight: 500,
                            lineHeight: '24px',
                            padding: '6px 12px!important',
                        }}
                        startIcon={
                            savedServiceParams.serviceCategory?.length > 0 && (
                                <Avatar
                                    sx={{
                                        bgcolor: 'white',
                                        color: 'primary.main',
                                        fontSize: '14px!important',
                                        padding: '2px 4px!important',
                                        fontWeight: '500!important',
                                        letterSpacing: '0.4px',
                                        lineHeight: '24px',
                                        minWidth: '22px',
                                        width: 'min-content!important',
                                        height: '22px',
                                        borderRadius: '100px',
                                    }}
                                >
                                    {savedServiceParams.serviceCategory?.length}
                                </Avatar>
                            )
                        }
                        endIcon={<ExpandMore />}
                    >
                        {getCategoryButtonText()}
                    </Button>
                    <Menu
                        id="category-menu"
                        anchorEl={anchorElCategory}
                        open={openCategory}
                        onClose={() => setAnchorElCategory(null)}
                        MenuListProps={{
                            'aria-labelledby': 'category-filter-button',
                        }}
                        PaperProps={{
                            style: {
                                maxHeight: '400px',
                            },
                        }}
                    >
                        {Array.isArray(serviceCategories) && serviceCategories?.length > 0 ? (
                            serviceCategories.map((category) => (
                                <MenuItem key={category._id} sx={styles.menuPadding}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={savedServiceParams.serviceCategory?.includes(category._id) || false}
                                                onChange={() => handleCategoryChange(category)} // handle only once
                                            />
                                        }
                                        label={category.name}
                                        sx={styles.formControlLabel}
                                    />
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem>No Category Available</MenuItem>
                        )}
                    </Menu>
                </Grid>

                {/* Vehicle Filter */}
                <Grid item md="auto">
                    <Button
                        variant={savedServiceParams.vehicleMake?.length > 0 ? 'contained' : 'text'}
                        id="category-filter-button"
                        aria-haspopup="true"
                        onClick={(event) => setAnchorElVehicle(event.currentTarget)}
                        sx={{
                            color: savedServiceParams.vehicleMake?.length > 0 ? 'white!important' : 'black!important',
                            fontWeight: 500,
                            lineHeight: '24px',
                            padding: '6px 12px!important',
                        }}
                        startIcon={
                            savedServiceParams.vehicleMake?.length > 0 && (
                                <Avatar
                                    sx={{
                                        bgcolor: 'white',
                                        color: 'primary.main',
                                        fontSize: '14px!important',
                                        padding: '2px 4px!important',
                                        fontWeight: '500!important',
                                        letterSpacing: '0.4px',
                                        lineHeight: '24px',
                                        minWidth: '22px',
                                        width: 'min-content!important',
                                        height: '22px',
                                        borderRadius: '100px',
                                    }}
                                >
                                    {savedServiceParams.vehicleMake?.length}
                                </Avatar>
                            )
                        }
                        endIcon={<ExpandMore />}
                    >
                        {getVehicleButtonText()}
                    </Button>
                    <Menu
                        id="category-menu"
                        anchorEl={anchorElVehicle}
                        open={openVehicle}
                        onClose={() => setAnchorElVehicle(null)}
                        MenuListProps={{
                            'aria-labelledby': 'category-filter-button',
                        }}
                        PaperProps={{
                            style: {
                                maxHeight: '400px',
                            },
                        }}
                    >
                        {Array.isArray(vehicleMake) && vehicleMake?.length > 0 ? (
                            vehicleMake.map((vehcile) => (
                                <MenuItem key={vehcile._id} sx={styles.menuPadding}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={savedServiceParams.vehicleMake?.includes(vehcile._id) || false}
                                                onChange={() => handleVehicleChange(vehcile)} // handle only once
                                            />
                                        }
                                        label={vehcile.name}
                                        sx={styles.formControlLabel}
                                    />
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem>No Vehcile Available</MenuItem>
                        )}
                    </Menu>
                </Grid>

                {showClearFilterButton && (
                    <Grid item md="auto">
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<Close />}
                            onClick={handleResetFilter}
                            sx={{
                                background: '#0000001A',
                                color: '#000000',
                                '&:hover': {
                                    background: '#0000001A',
                                    boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                                },
                                boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
                                fontWeight: 500,
                                lineHeight: '24px',
                            }}
                        >
                            {clearFilterLabel}
                        </Button>
                    </Grid>
                )}
            </Grid>

            {/* Input Search  */}
            <Grid item xs={12} sm={12} md={12} lg={6} xl={6} padding={'6px'} mt={{ sm: 2, xs: 2, md: 2, lg: 0 }}>
                <FormControl fullWidth>
                    <InputLabel htmlFor="outlined-adornment-search">Search</InputLabel>
                    <OutlinedInput
                        id="saved-service-search"
                        placeholder="Name, category, etc..."
                        startAdornment={
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        }
                        endAdornment={
                            savedServiceParams.search && (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => {
                                            setSavedServiceParams({
                                                ...savedServiceParams,
                                                search: '',
                                            });
                                            const search = document.getElementById('saved-service-search');
                                            if (search) {
                                                search.value = '';
                                            }
                                        }}
                                        edge="end"
                                        size="small"
                                    >
                                        <Close fontSize="14" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }
                        onChange={debounceSearch}
                        label="Search"
                    />
                </FormControl>
            </Grid>
        </Grid>
    );
};

export default SavedServicesFilters;
